import React, {FC} from 'react';
import {format, formatDistanceToNowStrict} from 'date-fns';
import {ArrowPathIcon, FunnelIcon} from '@heroicons/react/24/outline';
import {IWebhookDelivery, TWebhookDeliveryStatus} from '@verdocs/js-sdk';
import {Button, CodeBlock, RowAction, Spinner, StatTile, TBadgeTone} from '../Components';
import {useWebhookDelivery} from '../react-sdk/queries';
import {Dialog} from './Dialog';

export const DELIVERY_STATUS: Record<TWebhookDeliveryStatus, {label: string; tone: TBadgeTone}> = {
  delivered: {label: 'Delivered', tone: 'ok'},
  failed: {label: 'Failed', tone: 'warn'},
  pending: {label: 'Pending', tone: 'info'},
};

const prettyResponse = (response: string | null) => {
  if (!response) {
    return '(empty)';
  }

  try {
    return JSON.stringify(JSON.parse(response), null, 2);
  } catch {
    return response;
  }
};

interface WebhookDeliveryDialogProps {
  organizationId: string | undefined;
  delivery: IWebhookDelivery;
  resending: boolean;
  onResend: () => void;
  onFilterEnvelope: (envelopeId: string) => void;
  onClose: () => void;
}

export const WebhookDeliveryDialog: FC<WebhookDeliveryDialogProps> = ({
  organizationId,
  delivery: row,
  resending,
  onResend,
  onFilterEnvelope,
  onClose,
}) => {
  const detailQuery = useWebhookDelivery(organizationId, row.id);
  const detail = detailQuery.data;
  const delivery = detail || row;
  const {status, attempts, last_status, last_attempt_at, envelope_id} = delivery;

  return (
    <Dialog size="wide" onCancel={onClose}>
      <div className="text-lg font-medium text-[#33364b]">{delivery.event}</div>
      <div className="mb-4 text-[13px] text-gray-500">{format(new Date(delivery.created_at), 'MMM d, yyyy h:mm:ss a')}</div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="mb-4 grid grid-cols-3 gap-3">
          <StatTile
            label="Status"
            tone={status === 'delivered' ? 'ok' : status === 'failed' ? 'warn' : 'default'}
            value={DELIVERY_STATUS[status].label}
            hint={last_status ? `HTTP ${last_status}` : undefined}
          />
          <StatTile label="Attempts" value={attempts} />
          <StatTile
            label="Last attempt"
            value={last_attempt_at ? formatDistanceToNowStrict(new Date(last_attempt_at), {addSuffix: true}) : '-'}
            hint={last_attempt_at ? format(new Date(last_attempt_at), 'MMM d, h:mm a') : undefined}
          />
        </div>

        <div className="mb-4 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-1 text-[13px]">
          <div className="text-gray-500">Sent to</div>
          <div className="truncate font-mono text-[12px] text-[#33364b]">{delivery.url}</div>
          {envelope_id && (
            <>
              <div className="text-gray-500">Envelope</div>
              <div className="flex min-w-0 items-center gap-1">
                <span className="truncate font-mono text-[12px] text-[#33364b]">{envelope_id}</span>
                <RowAction title="Show deliveries for this envelope" onClick={() => onFilterEnvelope(envelope_id)}>
                  <FunnelIcon />
                </RowAction>
              </div>
            </>
          )}
        </div>

        {detailQuery.isError ? (
          <div className="text-[13px] text-gray-500">Unable to load this delivery. Please try again later.</div>
        ) : !detail ? (
          <Spinner mode="dark" />
        ) : (
          <>
            <div className="mb-2 text-[13px] font-medium text-[#33364b]">Request payload</div>
            <CodeBlock>
              {JSON.stringify(
                {
                  id: detail.id,
                  event: detail.event,
                  created_at: detail.created_at,
                  organization_id: detail.organization_id,
                  data: detail.body,
                },
                null,
                2,
              )}
            </CodeBlock>

            <div className="mb-2 mt-4 text-[13px] font-medium text-[#33364b]">Response</div>
            <CodeBlock maxHeight={140}>{prettyResponse(detail.last_result)}</CodeBlock>
          </>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#f2f3f5] pt-4">
        <div className="text-[12px] text-gray-500">Resending posts this payload, with the same id, to the endpoint's current URL.</div>
        <div className="flex gap-2">
          <Button size="small" variant="secondary" label="Close" onClick={onClose} />
          {status !== 'pending' && (
            <Button size="small" label="Resend" icon={<ArrowPathIcon className="h-4 w-4" />} disabled={resending} onClick={onResend} />
          )}
        </div>
      </div>
    </Dialog>
  );
};
